import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { BookmarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import MainLayout from '../../components/layout/MainLayout';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import AutoRefreshToggle, { AutoRefreshConfig } from '../../components/common/AutoRefreshToggle';
import AdvancedFilterBuilder, { FilterGroup } from '../../components/analytics/AdvancedFilterBuilder';
import SaveCohortModal from '../../components/analytics/SaveCohortModal';
import productAnalyticsService, { type Cohort, type RetentionMatrix } from '../../services/api/product-analytics.service';

type FilterMode = 'saved' | 'custom';

export default function RetentionPage() {
  const { t } = useTranslation('retention');
  const { t: tCommon } = useTranslation('common');
  const selectedAppId = useAppSelector(selectCurrentApplication);
  const [filterMode, setFilterMode] = useState<FilterMode>('custom');

  // Saved cohort mode
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [selectedCohortId, setSelectedCohortId] = useState<string>('');

  // Custom filter mode
  const [customFilters, setCustomFilters] = useState<FilterGroup>({
    id: 'root',
    logicOperator: 'AND',
    conditions: [],
    groups: [],
  });
  const [userCountPreview, setUserCountPreview] = useState<number>(0);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const [retentionData, setRetentionData] = useState<RetentionMatrix | null>(null);
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('week');
  const [periods, setPeriods] = useState<number>(12);
  const [loading, setLoading] = useState(false);
  const [loadingCohorts, setLoadingCohorts] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 30 * 24 * 60 * 60 * 1000,
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '30d',
  });

  const [autoRefresh, setAutoRefresh] = useState<AutoRefreshConfig>({
    enabled: false,
    interval: 60000,
  });

  // Load cohorts when app changes
  useEffect(() => {
    loadCohorts();
  }, [selectedAppId]);

  // Auto-load retention in custom mode (on first visit + when settings change)
  useEffect(() => {
    if (filterMode === 'custom') {
      loadRetentionWithFilters();
    }
  }, [filterMode, groupBy, periods, timeRange.startTime, timeRange.endTime, selectedAppId]);

  // Preview user count when custom filters change
  useEffect(() => {
    if (filterMode === 'custom') {
      previewUserCount();
    }
  }, [customFilters, selectedAppId, filterMode]);

  // Auto-load retention when cohort is selected
  useEffect(() => {
    if (filterMode === 'saved' && selectedCohortId) {
      loadRetentionWithCohort();
    }
  }, [selectedCohortId, groupBy, periods]);

  // Auto-refresh setup
  useEffect(() => {
    if (!autoRefresh.enabled) return;

    const interval = setInterval(() => {
      if (timeRange.rangeType === 'preset' && timeRange.presetValue) {
        const now = Date.now();
        const duration = parseDuration(timeRange.presetValue);
        setTimeRange({
          ...timeRange,
          startTime: now - duration,
          endTime: now,
        });
      } else {
        if (filterMode === 'saved' && selectedCohortId) {
          loadRetentionWithCohort();
        } else if (filterMode === 'custom') {
          loadRetentionWithFilters();
        }
      }
    }, autoRefresh.interval);

    return () => clearInterval(interval);
  }, [autoRefresh.enabled, autoRefresh.interval, timeRange.rangeType, selectedCohortId, filterMode, customFilters]);

  const parseDuration = (preset: string): number => {
    const value = parseInt(preset);
    if (preset.endsWith('m')) return value * 60 * 1000;
    if (preset.endsWith('h')) return value * 60 * 60 * 1000;
    if (preset.endsWith('d')) return value * 24 * 60 * 60 * 1000;
    return value;
  };

  const loadCohorts = async () => {
    try {
      setLoadingCohorts(true);
      const data = await productAnalyticsService.listCohorts(selectedAppId || 'all');
      setCohorts(data || []);

      // Auto-select first cohort if available
      if (data && data.length > 0 && !selectedCohortId) {
        setSelectedCohortId(data[0].id);
      }
    } catch (err: any) {
      console.error('Error loading cohorts:', err);
      setError(err.message || 'Failed to load cohorts');
    } finally {
      setLoadingCohorts(false);
    }
  };

  const previewUserCount = async () => {
    try {
      setLoadingPreview(true);
      const result = await productAnalyticsService.previewFilteredUsers(
        selectedAppId || 'all',
        customFilters
      );
      setUserCountPreview(result.userCount);
    } catch (err: any) {
      console.error('Error previewing users:', err);
      setUserCountPreview(0);
    } finally {
      setLoadingPreview(false);
    }
  };

  const loadRetentionWithCohort = async () => {
    if (!selectedCohortId) {
      console.log('[Retention] No cohort selected, skipping load');
      return;
    }

    console.log('[Retention] Loading with cohort:', selectedCohortId, 'groupBy:', groupBy, 'periods:', periods);

    try {
      setLoading(true);
      setError(null);
      const data = await productAnalyticsService.getRetentionAnalysis(
        selectedCohortId,
        groupBy,
        periods
      );
      console.log('[Retention] Received data:', data);
      setRetentionData(data);
    } catch (err: any) {
      console.error('[Retention] Error loading retention analysis:', err);
      setError(err.message || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const loadRetentionWithFilters = async () => {
    console.log('[Retention] Loading with custom filters:', customFilters, 'groupBy:', groupBy, 'periods:', periods);

    try {
      setLoading(true);
      setError(null);
      const data = await productAnalyticsService.analyzeRetentionWithFilters(
        selectedAppId || 'all',
        customFilters,
        groupBy,
        periods,
        new Date(timeRange.startTime).toISOString(),
        new Date(timeRange.endTime).toISOString()
      );
      console.log('[Retention] Received filtered data:', data);
      setRetentionData(data);
    } catch (err: any) {
      console.error('[Retention] Error loading retention with filters:', err);
      setError(err.message || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    console.log('[Retention] Applying filters - Mode:', filterMode, 'CohortId:', selectedCohortId, 'CustomFilters:', customFilters);
    if (filterMode === 'saved') {
      loadRetentionWithCohort();
    } else {
      loadRetentionWithFilters();
    }
  };

  const handleSaveCohort = async (name: string, description: string) => {
    if (!selectedAppId) {
      toast.error('Please select a specific application to save a cohort.', {
        duration: 4000,
        icon: '⚠️',
      });
      return;
    }

    await productAnalyticsService.createCohort({
      applicationId: selectedAppId,
      name,
      description,
      criteria: customFilters,
    });

    // Reload cohorts list
    await loadCohorts();

    // Optionally switch to saved mode and select the new cohort
    setFilterMode('saved');
  };

  // Convert API data to chart format
  const getRetentionCurveData = () => {
    if (!retentionData || !retentionData.cohorts || retentionData.cohorts.length === 0) {
      return [];
    }

    const periods = Object.keys(retentionData.cohorts[0].retentionByPeriod).map(Number).sort((a, b) => a - b);

    return periods.map((period) => {
      const dataPoint: any = {
        period: `${groupBy === 'day' ? t('settings.day') : groupBy === 'week' ? t('settings.week') : t('settings.month')} ${period}`,
      };

      retentionData.cohorts.forEach((cohort, index) => {
        dataPoint[`cohort${index + 1}`] = cohort.retentionByPeriod[period] || null;
      });

      // Calculate average
      const values = retentionData.cohorts
        .map(c => c.retentionByPeriod[period])
        .filter(v => v !== undefined && v !== null);

      if (values.length > 0) {
        dataPoint.average = values.reduce((sum, val) => sum + val, 0) / values.length;
      }

      return dataPoint;
    });
  };

  const retentionCurveData = getRetentionCurveData();

  const getRetentionColor = (value: number): string => {
    if (value === 0) return 'bg-gray-100 dark:bg-gray-700';
    if (value >= 50) return 'bg-green-500';
    if (value >= 40) return 'bg-green-400';
    if (value >= 30) return 'bg-yellow-400';
    if (value >= 20) return 'bg-orange-400';
    return 'bg-red-400';
  };

  const getRetentionTextColor = (value: number): string => {
    if (value === 0) return 'text-gray-400';
    return 'text-white';
  };

  const canApplyFilters = () => {
    if (filterMode === 'saved') {
      return !!selectedCohortId;
    } else {
      // In custom filter mode, can always apply (empty filters = all users)
      return true;
    }
  };

  const canSaveCohort = () => {
    return filterMode === 'custom' && (customFilters.conditions.length > 0 || customFilters.groups.length > 0);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('title')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('subtitle')}
          </p>
        </div>

        {/* Time Range Selector */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>

        {/* Auto Refresh Toggle */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <AutoRefreshToggle
            value={autoRefresh}
            onChange={setAutoRefresh}
            onManualRefresh={() => {
              if (filterMode === 'saved' && selectedCohortId) {
                loadRetentionWithCohort();
              } else {
                loadRetentionWithFilters();
              }
            }}
          />
        </div>

        {/* Filter Mode Toggle and Filters */}
        <>
          {/* Filter Mode Selection */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-4 mb-6">
                <button
                  onClick={() => setFilterMode('saved')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterMode === 'saved'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <BookmarkIcon className="w-5 h-5" />
                  {t('filters.useSavedCohort')}
                </button>
                <button
                  onClick={() => setFilterMode('custom')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterMode === 'custom'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <FunnelIcon className="w-5 h-5" />
                  {t('filters.customFilters')}
                </button>
              </div>

              {filterMode === 'saved' ? (
                /* Saved Cohort Selection */
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t('cohort.select')}
                  </label>
                  {loadingCohorts ? (
                    <div className="text-center py-4">
                      <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                    </div>
                  ) : cohorts.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                      {t('cohort.noCohorts')}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {cohorts.map((cohort) => (
                        <div
                          key={cohort.id}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                            selectedCohortId === cohort.id
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-300 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-700'
                          }`}
                          onClick={() => setSelectedCohortId(cohort.id)}
                        >
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-white">{cohort.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {cohort.user_count} users
                              {cohort.description && ` • ${cohort.description}`}
                            </p>
                          </div>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete cohort "${cohort.name}"?`)) {
                                try {
                                  await productAnalyticsService.deleteCohort(cohort.id);
                                  toast.success('Cohort deleted successfully');
                                  if (selectedCohortId === cohort.id) {
                                    setSelectedCohortId('');
                                  }
                                  await loadCohorts();
                                } catch (err: any) {
                                  toast.error(err.message || 'Failed to delete cohort');
                                }
                              }
                            }}
                            className="ml-3 p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            title="Delete cohort"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                /* Custom Filters */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('filters.title')}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {t('filters.subtitle')}
                      </p>
                    </div>
                    {loadingPreview ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        {t('filters.loadingPreview')}
                      </div>
                    ) : userCountPreview > 0 ? (
                      <div className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-medium">
                        {customFilters.conditions.length === 0 && customFilters.groups.length === 0
                          ? `${userCountPreview} ${t('filters.allUsers', 'total users')}`
                          : t('filters.matchingUsers', { count: userCountPreview })}
                      </div>
                    ) : null}
                  </div>

                  <AdvancedFilterBuilder
                    value={customFilters}
                    onChange={setCustomFilters}
                    propertyOptions={[
                      { value: 'user.email', label: 'User Email', type: 'string' },
                      { value: 'user.name', label: 'User Name', type: 'string' },
                      { value: 'user.id', label: 'User ID', type: 'string' },
                      { value: 'user.first_seen', label: 'First Seen', type: 'date' },
                      { value: 'user.last_seen', label: 'Last Seen', type: 'date' },
                      { value: 'user.session_count', label: 'Session Count', type: 'number' },
                      { value: 'user.total_events', label: 'Total Events', type: 'number' },
                      { value: 'event.name', label: 'Event Name', type: 'string' },
                      { value: 'event.count', label: 'Event Count', type: 'number' },
                    ]}
                  />

                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleApplyFilters}
                      disabled={!canApplyFilters() || loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                    >
                      {loading ? tCommon('loading') : t('filters.applyFilters')}
                    </button>
                    <button
                      onClick={() => setShowSaveModal(true)}
                      disabled={!canSaveCohort()}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                    >
                      {t('filters.saveAsCohort')}
                    </button>
                    <button
                      onClick={() => setCustomFilters({ id: 'root', logicOperator: 'AND', conditions: [], groups: [] })}
                      disabled={customFilters.conditions.length === 0 && customFilters.groups.length === 0}
                      className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                    >
                      {t('filters.clearFilters')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Analysis Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                {t('settings.groupBy')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Group By */}
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {t('settings.groupBy')}
                  </label>
                  <select
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value as 'day' | 'week' | 'month')}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="day">{t('settings.day')}</option>
                    <option value="week">{t('settings.week')}</option>
                    <option value="month">{t('settings.month')}</option>
                  </select>
                </div>

                {/* Periods */}
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {t('settings.periods')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="24"
                    value={periods}
                    onChange={(e) => setPeriods(Math.max(1, Math.min(24, parseInt(e.target.value) || 12)))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder={t('settings.periodsHint')}
                  />
                </div>
              </div>
            </div>
        </>

        {/* Loading/Error States */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">{t('loading')}</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {tCommon('retry')}
            </button>
          </div>
        ) : !retentionData ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <svg className="mx-auto w-16 h-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              {t('empty.noData')}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('empty.noDataSubtitle')}
            </p>
          </div>
        ) : (
          <>
            {/* Retention Curve Chart */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                {t('chart.title')}
              </h2>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart
                  data={retentionCurveData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                  <XAxis
                    dataKey="period"
                    stroke="#9CA3AF"
                    style={{ fontSize: '12px' }}
                    tick={{ fontSize: 11 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    stroke="#9CA3AF"
                    style={{ fontSize: '12px' }}
                    domain={[0, 100]}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F3F4F6',
                    }}
                    formatter={(value: any) => [`${value?.toFixed(1)}%`, '']}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: '20px' }}
                    iconType="line"
                  />
                  {retentionData && retentionData.cohorts.slice(0, 4).map((cohort, index) => {
                    const colors = ['#22D3EE', '#6366F1', '#A855F7', '#EC4899'];
                    // Format the cohort date for display
                    const formattedDate = cohort.cohortDate
                      ? new Date(cohort.cohortDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : `Cohort ${index + 1}`;
                    return (
                      <Line
                        key={index}
                        type="monotone"
                        dataKey={`cohort${index + 1}`}
                        stroke={colors[index]}
                        strokeWidth={2}
                        name={formattedDate}
                        dot={{ fill: colors[index], r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    );
                  })}
                  <Line
                    type="monotone"
                    dataKey="average"
                    stroke="#F59E0B"
                    strokeWidth={3}
                    name="Average"
                    strokeDasharray="5 5"
                    dot={{ fill: '#F59E0B', r: 5 }}
                    activeDot={{ r: 7 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Retention Matrix */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('matrix.title')} - {retentionData.cohortName}
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {tCommon('groupedBy')} {groupBy}
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('matrix.cohort')}
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('matrix.users')}
                      </th>
                      {Object.keys(retentionData.cohorts[0]?.retentionByPeriod || {}).sort((a, b) => Number(a) - Number(b)).map((period) => (
                        <th key={period} className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {t(`matrix.${groupBy}`, { number: period })}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {retentionData.cohorts.map((cohort, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                          {new Date(cohort.cohortDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-gray-600 dark:text-gray-400">
                          {cohort.totalUsers}
                        </td>
                        {Object.keys(cohort.retentionByPeriod).sort((a, b) => Number(a) - Number(b)).map((period) => {
                          const value = cohort.retentionByPeriod[Number(period)];
                          return (
                            <td key={period} className="px-6 py-4 whitespace-nowrap text-center">
                              {value !== undefined && value !== null ? (
                                <div className={`inline-flex items-center justify-center min-w-[60px] px-3 py-2 rounded ${getRetentionColor(value)} ${getRetentionTextColor(value)} font-semibold text-sm`}>
                                  {value.toFixed(1)}%
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Save Cohort Modal */}
      <SaveCohortModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveCohort}
        filters={customFilters}
        userCount={userCountPreview}
        applicationId={selectedAppId}
      />
    </MainLayout>
  );
}
